import "../styles/TemplateDivisionElement.css";
import TemplateDivisionTypeElement from "./TemplateDivisionTypeElement";
import {
    faCaretDown,
    faCaretRight,
    faCirclePlus,
    faFolderPlus,
    faPencil,
    faTrash
} from "@fortawesome/free-solid-svg-icons";
import {useContext, useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ContextMenu, ContextMenuItem} from "./ContextMenu";
import {ColumnGrid, GridRow} from "./ColumnGrid";
import {findDivision, findDivisionType, SelectedDivisionContext, TemplateContext} from "../pages/MapTemplateEditor";
import axios from "axios";
import Button from "./Button";
import Modal from "./Modal";
import TextInput from "./TextInput";
import _ from "lodash";

import config from '../config.json';

const InlineIconButton = ({ icon, onClick, style }) => {
    return <div style={style} className="InlineIconButton" onClick={onClick}><FontAwesomeIcon icon={icon}/></div>;
}

const DivisionTypeCreateModal = ({ showModal, setShowModal, divisionId }) => {
    const { template, setTemplate } = useContext(TemplateContext);
    const [ name, setName ] = useState();
    const [ pluralName, setPluralName ] = useState();

    const create = async () => {
        setShowModal(false);

        const modifiedTemplate = _.cloneDeep(template);

        const { result: division } = findDivision(divisionId, modifiedTemplate);
        division.division_types.push({ name, pluralName, topLevel: true });

        setTemplate(modifiedTemplate);
    };

    const footer = (
        <div style={{ display: "flex", gap: "20px" }}>
            <Button color="#40b319" onClick={create} content="Create"/>
            <Button color="#c20a0a" onClick={() => setShowModal(false)} content="Cancel"/>
        </div>
    );

    return (
        <Modal
            header="New Division Type"
            footer={footer}
            show={showModal}
            onClose={() => setShowModal(false)}>
            <ColumnGrid columns="1fr 1fr">
                <GridRow>
                    <span>Name</span>
                    <TextInput onChange={e => setName(e.target.value)}/>
                </GridRow>
                <GridRow>
                    <span>Plural Name</span>
                    <TextInput onChange={e => setPluralName(e.target.value)}/>
                </GridRow>
            </ColumnGrid>
        </Modal>
    );
}

const RenameModal = ({ showModal, setShowModal, divisionId }) => {
    const { template, setTemplate } = useContext(TemplateContext);
    const [ name, setName ] = useState();

    const rename = async () => {
        setShowModal(false);

        const { result: division } = findDivision(divisionId, template);
        division.name = name;

        setTemplate(template);
    };

    const footer = (
        <div style={{ display: "flex", gap: "20px" }}>
            <Button color="#40b319" onClick={rename} content="Rename"/>
            <Button color="#c20a0a" onClick={() => setShowModal(false)} content="Cancel"/>
        </div>
    );

    return (
        <Modal
            header="Rename"
            footer={footer}
            show={showModal}
            onClose={() => setShowModal(false)}>
            <ColumnGrid columns="1fr 1fr">
                <GridRow>
                    <span>Name</span>
                    <TextInput onChange={e => setName(e.target.value)}/>
                </GridRow>
            </ColumnGrid>
        </Modal>
    );
}

const LocalContextMenuItem = ({icon, name, keystroke, onClick }) => {
    return (
        <ContextMenuItem onClick={onClick}>
            <ColumnGrid columns={"30px 1fr 1fr"}>
                <GridRow>
                    <FontAwesomeIcon icon={icon} style={{ margin: "auto" }}/>
                    <span>{name}</span>
                    <span style={{ textAlign: "right", fontWeight: 800 }}>{keystroke}</span>
                </GridRow>
            </ColumnGrid>
        </ContextMenuItem>
    );
}

const DivisionCreateModal = ({ showModal, setShowModal, divisionId }) => {
    const { template, setTemplate } = useContext(TemplateContext);
    const [ name, setName ] = useState();
    const [ tipe, setTipe ] = useState();

    const create = async () => {
        setShowModal(false);

        const modifiedTemplate = _.cloneDeep(template);

        const { result: division } = findDivision(divisionId, modifiedTemplate);
        division.divisions.push({ name, type: tipe });

        setTemplate(modifiedTemplate);
    };

    const [ divisionData, setDivisionData ] = useState(null);
    const [ divisionType, setDivisionType ] = useState(null);

    useEffect(() => {
        const data = findDivision(divisionId, template);
        const tipe = findDivisionType(data?.result.type, template);

        setDivisionData(data);
        setDivisionType(tipe);

        setTipe(data?.result.division_types?.[0]?.id ?? tipe?.result.division_types?.[0]?.id);
    }, [divisionId, template]);

    if (!divisionData) return;

    const footer = (
        <div style={{ display: "flex", gap: "20px" }}>
            <Button color="#40b319" onClick={create} content="Create"/>
            <Button color="#c20a0a" onClick={() => setShowModal(false)} content="Cancel"/>
        </div>
    );

    const InternalOptionGroup = ({ divisionTypes, label }) => {
        if (!divisionTypes?.length) return null;

        return (
            <optgroup label={label}>
                {divisionTypes.map(divisionType => <option key={divisionType.id} value={divisionType.id}>{divisionType.name}</option>)}
            </optgroup>
        )
    }

    return (
        <Modal
            header="New Division"
            footer={footer}
            show={showModal}
            onClose={() => setShowModal(false)}>
            <ColumnGrid columns="1fr 1fr">
                <GridRow>
                    <span>Name</span>
                    <TextInput onChange={e => setName(e.target.value)}/>
                </GridRow>
                <GridRow>
                    <span>Type</span>
                    <select onChange={e => setTipe(e.target.value)}>
                        <InternalOptionGroup divisionTypes={divisionData.result.division_types} label="Local"/>
                        <InternalOptionGroup divisionTypes={divisionType.result.division_types} label="Global"/>
                    </select>
                </GridRow>
            </ColumnGrid>
        </Modal>
    );
}

const TemplateDivisionElement = ({ id, parentOpen = true }) => {
    const [ open, setOpen ] = useState(false);

    const { selectedDivision, setSelectedDivision } = useContext(SelectedDivisionContext);

    const [ showTypeModal, setShowTypeModal ] = useState(false);
    const [ showDivisionModal, setShowDivisionModal ] = useState(false);
    const [ showRenameModal, setShowRenameModal ] = useState(false);

    const [ showContextMenu, setShowContextMenu ] = useState(false);

    const { template } = useContext(TemplateContext);

    if (parentOpen === false && open) setOpen(false);

    const [ divisionData, setDivisionData ] = useState(null);
    const [ divisionType, setDivisionType ] = useState(null);

    useEffect(() => {
        const data = findDivision(id, template);
        const tipe = findDivisionType(data?.result.type, template);

        setDivisionType(tipe);
        setDivisionData(data);
    }, [id, template]);

    if (!divisionData || !divisionData.result) return;

    return (
        <>
            <div className="TemplateDivisionElement">
                <DivisionTypeCreateModal showModal={showTypeModal} setShowModal={setShowTypeModal} divisionId={id}/>
                <DivisionCreateModal showModal={showDivisionModal} setShowModal={setShowDivisionModal} divisionId={id}/>
                <RenameModal showModal={showRenameModal} setShowModal={setShowRenameModal} divisionId={id}/>

                <ContextMenu show={showContextMenu} setShow={setShowContextMenu}>
                    <LocalContextMenuItem icon={faFolderPlus} name="New Subtype" keystroke={"⇧N"} onClick={() => {setShowContextMenu(false); setShowTypeModal(true)}}/>
                    {(divisionData.result.division_types.length || divisionType.result.division_types.length) ? <LocalContextMenuItem icon={faCirclePlus} name="New Subdivision" keystroke={"N"} onClick={() => {setShowContextMenu(false); setShowDivisionModal(true)}}/> : <></>}
                    <LocalContextMenuItem icon={faPencil} name="Rename" keystroke={"R"} onClick={() => {setShowContextMenu(false); setShowRenameModal(true)}}/>
                    <LocalContextMenuItem icon={faTrash} name="Delete" keystroke={"⌫"}/>
                </ContextMenu>

                <div className={`self ${selectedDivision === id ? 'selected' : ''}`} tabIndex={0} style={{ paddingLeft: ((1 + divisionData.depth) * 20) + 'px' }}
                     onClick={e => { e.stopPropagation(); setSelectedDivision(id)}}
                     onContextMenu={e => {setShowContextMenu({ x: e.nativeEvent.clientX, y: e.nativeEvent.clientY }); e.preventDefault();}}
                     onKeyUp={e => {
                         e.stopPropagation();
                         e.preventDefault();
                         setShowContextMenu(false);

                         if (e.key === 'N') setShowTypeModal(true);
                         if (e.key === 'n') setShowDivisionModal(true);
                         if (e.key === 'r') setShowRenameModal(true);
                     }}>
                    <InlineIconButton icon={ open ? faCaretDown : faCaretRight } onClick={() => setOpen(!open)}
                                      style={{ opacity: divisionData.result.division_types?.length || divisionData.result.divisions?.length ? '100%' : 0 }}/>
                    <span className="name">{divisionData.result.name}</span>
                    <span className="type">:{divisionType.result.name}</span>
                </div>
                <div className="children" style={{ display: open ? "block" : "none" }}>
                    {divisionData.result.division_types?.map(divisionType => <TemplateDivisionTypeElement key={divisionType.id} parentOpen={open} id={divisionType.id}/>)}
                    {divisionData.result.divisions?.map(division => <TemplateDivisionElement key={division.id} parentOpen={open} id={division.id}/>)}
                </div>
            </div>
        </>
    );
}

export default TemplateDivisionElement;
